import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/cn";
import { Separator } from "@/shared/ui/kit/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/kit/tooltip";
import { useSidebar } from "@/shared/ui/kit/sidebar-context";

export const SidebarHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} data-sidebar="header" className={cn("flex flex-col gap-2 p-3", className)} {...props} />
));
SidebarHeader.displayName = "SidebarHeader";

export const SidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} data-sidebar="footer" className={cn("mt-auto flex flex-col gap-2 p-3", className)} {...props} />
));
SidebarFooter.displayName = "SidebarFooter";

export const SidebarSeparator = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof Separator>>(
    ({ className, ...props }, ref) => (
        <Separator ref={ref} data-sidebar="separator" className={cn("mx-3 w-auto bg-white/10 dark:bg-white/10", className)} {...props} />
    )
);
SidebarSeparator.displayName = "SidebarSeparator";

export const SidebarContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        data-sidebar="content"
        className={cn("flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden py-2 sidebar-scrollbar", className)}
        {...props}
    />
));
SidebarContent.displayName = "SidebarContent";

export const SidebarMenu = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        data-sidebar="menu"
        className={cn("flex w-full min-w-0 flex-col gap-0.5 group-data-[collapsible=icon]:items-center", className)}
        {...props}
    />
));
SidebarMenu.displayName = "SidebarMenu";

export const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.LiHTMLAttributes<HTMLLIElement>>(({ className, ...props }, ref) => (
    <li ref={ref} data-sidebar="menu-item" className={cn("relative", className)} {...props} />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
    "peer/menu-button flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 text-left text-sm font-medium text-white/70 outline-none transition-[width,height,padding,background-color,color] duration-base hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-nav_accent data-[active=true]:bg-dark_green data-[active=true]:text-nav_active_fg [&>svg]:size-5 [&>svg]:shrink-0 [&>span:last-child]:truncate group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>span]:hidden",
    {
        variants: {
            size: {
                default: "h-11",
                lg: "h-14",
            },
        },
        defaultVariants: {
            size: "default",
        },
    }
);

interface SidebarMenuButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof sidebarMenuButtonVariants> {
    asChild?: boolean;
    isActive?: boolean;
    /** Rotulo mostrado so no modo icone (desktop recolhido). */
    tooltip?: string;
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
    ({ asChild = false, isActive = false, size, tooltip, className, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        const { isMobile, state } = useSidebar();

        const button = (
            <Comp
                ref={ref}
                data-sidebar="menu-button"
                data-active={isActive}
                aria-current={isActive ? "page" : undefined}
                className={cn(sidebarMenuButtonVariants({ size }), className)}
                {...props}
            />
        );

        if (!tooltip) return button;

        return (
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right" align="center" hidden={state !== "collapsed" || isMobile}>
                    {tooltip}
                </TooltipContent>
            </Tooltip>
        );
    }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

export const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        data-sidebar="menu-sub"
        className={cn("ml-5 flex min-w-0 flex-col gap-0.5 border-l border-white/10 py-1 pl-3 group-data-[collapsible=icon]:hidden", className)}
        {...props}
    />
));
SidebarMenuSub.displayName = "SidebarMenuSub";

export const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.LiHTMLAttributes<HTMLLIElement>>((props, ref) => (
    <li ref={ref} data-sidebar="menu-sub-item" {...props} />
));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

interface SidebarMenuSubButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    asChild?: boolean;
    isActive?: boolean;
}

export const SidebarMenuSubButton = React.forwardRef<HTMLAnchorElement, SidebarMenuSubButtonProps>(
    ({ asChild = false, isActive = false, className, ...props }, ref) => {
        const Comp = asChild ? Slot : "a";
        return (
            <Comp
                ref={ref}
                data-sidebar="menu-sub-button"
                data-active={isActive}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                    "flex h-9 touch:h-11 min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-[13px] text-white/70 outline-none transition-colors hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-nav_accent data-[active=true]:font-semibold data-[active=true]:text-nav_accent [&>span:last-child]:truncate",
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";
